/**
 * Модуль для работы с транзакциями и роялти
 */

import { logger } from "../../utils/logger.js";

/**
 * Форматирует дату для отображения
 */
function formatTransactionDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Создаёт менеджер для работы с транзакциями
 */
export function createTransactionsManager({
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  escapeHtmlFn,
  documentRef = document,
}) {
  /**
   * Загружает историю транзакций
   */
  async function loadTransactions() {
    const domCache = window.__domCacheManager;
    const loadingManager = window.__loadingIndicatorManager;
    
    const container = domCache?.getCachedElement("#transactions-list", documentRef) 
      || documentRef.getElementById("transactions-list");
    
    if (!container) {
      logger.error("Элемент transactions-list не найден");
      return;
    }

    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp || !telegramWebApp.initData) {
      const errorHTML = '<div class="error">Ошибка: Telegram не инициализирован</div>';
      domCache?.updateHTML("#transactions-list", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("transactions-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      const loadingHTML = '<div class="loading">Загрузка...</div>';
      domCache?.updateHTML("#transactions-list", loadingHTML, documentRef) ||
      (container.innerHTML = loadingHTML);
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/transactions/history`, {
        headers: buildAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: "Неизвестная ошибка" }));
        const errorHTML = `<div class="error">Ошибка загрузки истории: ${error.message || response.statusText}</div>`;
        domCache?.updateHTML("#transactions-list", errorHTML, documentRef) ||
        (container.innerHTML = errorHTML);
        if (loadingManager) loadingManager.hideLoading("transactions-load");
        return;
      }

      const data = await response.json();
      if (data.success) {
        container.innerHTML = "";

        if (!data.transactions || data.transactions.length === 0) {
          const emptyHTML = '<div class="loading">История транзакций пуста</div>';
          domCache?.updateHTML("#transactions-list", emptyHTML, documentRef) ||
          (container.innerHTML = emptyHTML);
          if (loadingManager) loadingManager.hideLoading("transactions-load");
          return;
        }

        data.transactions.forEach((transaction) => {
          const item = documentRef.createElement("div");
          item.className = "gift-item";
          item.style.marginBottom = "12px";

          const date = formatTransactionDate(transaction.created_at);
          let html = "";

          if (transaction.type === "purchase") {
            if (transaction.subtype === "creature") {
              const creatureName = escapeHtmlFn(
                transaction.meta?.creature_name || "Существо",
              );
              const royalty = transaction.meta?.royalty;
              html = `
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="font-size: 24px;">🛍️</span>
                  <div>
                    <div style="font-weight: 600;">Покупка существа</div>
                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                      ${creatureName} | ${date}
                    </div>
                  </div>
                </div>
                ${
                  royalty?.coins
                    ? `
                  <div style="font-size: 12px; color: var(--tg-theme-link-color); margin-top: 8px;">
                    💰 Роялти: ${royalty.coins} монет
                  </div>
                `
                    : ""
                }
                <div style="font-size: 16px; font-weight: 600; color: var(--tg-theme-text-color); text-align: right; margin-top: 8px;">
                  -${transaction.amount} ⭐
                </div>
              `;
            } else {
              html = `
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="font-size: 24px;">⭐</span>
                  <div>
                    <div style="font-weight: 600;">Покупка звёзд</div>
                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                      ${date}
                    </div>
                  </div>
                </div>
                <div style="font-size: 16px; font-weight: 600; color: var(--tg-theme-text-color); text-align: right; margin-top: 8px;">
                  +${transaction.amount} ⭐
                </div>
              `;
            }
          } else if (transaction.type === "gift") {
            if (transaction.subtype === "sent") {
              const statusIcon =
                transaction.status === "accepted"
                  ? "✓"
                  : transaction.status === "declined"
                    ? "✗"
                    : "⏳";
              const recipient = escapeHtmlFn(transaction.recipient || "пользователь");
              const creatureName = escapeHtmlFn(transaction.creature_name || "");
              const message = transaction.message
                ? escapeHtmlFn(transaction.message)
                : null;

              html = `
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="font-size: 24px;">🎁</span>
                  <div>
                    <div style="font-weight: 600;">Подарок отправлен</div>
                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                      Кому: @${recipient} | ${creatureName} | ${date}
                    </div>
                  </div>
                </div>
                ${
                  message
                    ? `
                  <div style="font-size: 14px; margin-top: 8px; padding: 8px; background: var(--tg-theme-secondary-bg-color); border-radius: 6px;">
                    💬 "${message}"
                  </div>
                `
                    : ""
                }
                <div style="font-size: 12px; color: var(--tg-theme-hint-color); text-align: right; margin-top: 8px;">
                  ${statusIcon}
                </div>
              `;
            } else {
              const sender = escapeHtmlFn(transaction.sender || "пользователь");
              const creatureName = escapeHtmlFn(transaction.creature_name || "");
              const message = transaction.message
                ? escapeHtmlFn(transaction.message)
                : null;
              const royalty = transaction.royalty;

              html = `
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="font-size: 24px;">🎁</span>
                  <div>
                    <div style="font-weight: 600;">Подарок получен</div>
                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                      От: @${sender} | ${creatureName} | ${date}
                    </div>
                  </div>
                </div>
                ${
                  message
                    ? `
                  <div style="font-size: 14px; margin-top: 8px; padding: 8px; background: var(--tg-theme-secondary-bg-color); border-radius: 6px;">
                    💬 "${message}"
                  </div>
                `
                    : ""
                }
                ${
                  royalty?.coins
                    ? `
                  <div style="font-size: 12px; color: var(--tg-theme-link-color); margin-top: 8px;">
                    💰 Роялти отправителю: ${royalty.coins} монет
                  </div>
                `
                    : ""
                }
                <div style="font-size: 12px; color: var(--tg-theme-hint-color); text-align: right; margin-top: 8px;">
                  ✓
                </div>
              `;
            }
          }

          item.innerHTML = html;
          container.appendChild(item);
        });
      }
    } catch (error) {
      logger.error("Load transactions error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки истории</div>';
      domCache?.updateHTML("#transactions-list", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
      if (loadingManager) loadingManager.hideLoading("transactions-load");
    } finally {
      if (loadingManager) loadingManager.hideLoading("transactions-load");
    }
  }

  /**
   * Загружает статистику роялти
   */
  async function loadRoyaltyStats() {
    const domCache = window.__domCacheManager;
    const container = domCache?.getCachedElement("#royalty-stats-content", documentRef) 
      || documentRef.getElementById("royalty-stats-content");
    
    if (!container) {
      logger.error("Элемент royalty-stats-content не найден");
      return;
    }

    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp || !telegramWebApp.initData) {
      container.innerHTML =
        '<div class="error">Ошибка: Telegram не инициализирован</div>';
      return;
    }

    container.innerHTML = '<div class="loading">Загрузка...</div>';

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/royalty/stats`, {
        headers: buildAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: "Неизвестная ошибка" }));
        container.innerHTML = `<div class="error">Ошибка загрузки статистики: ${error.message || response.statusText}</div>`;
        return;
      }

      const data = await response.json();
      if (data.success && data.stats) {
        const stats = data.stats;
        const formatNumber = (value) =>
          (Number.isFinite(value) ? value : Number(value) || 0).toLocaleString(
            "ru-RU",
          );

        container.innerHTML = `
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-value">${formatNumber(stats.total_royalty)}</div>
              <div class="stat-label">Всего получено роялти</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${formatNumber(stats.received_from_purchases)}</div>
              <div class="stat-label">Кэшбэк с покупок</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${formatNumber(stats.received_from_gifts)}</div>
              <div class="stat-label">Получено с подарков</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${formatNumber(stats.sent_to_others)}</div>
              <div class="stat-label">Отправлено другим</div>
            </div>
          </div>
        `;
      }
    } catch (error) {
      logger.error("Load royalty stats error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки статистики</div>';
      domCache?.updateHTML("#royalty-stats-content", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
    }
  }

  return {
    loadTransactions,
    loadRoyaltyStats,
  };
}














